import { useMemo, useState } from 'react';

import { useCanTranslateContent } from '~/hooks/useCanTranslateContent';
import { useConnectionDeliveryLocales } from '~/hooks/useConnectionDeliveryLocales';

import type { RunTranslationSummary } from '../types/translations.types';

import { useRunTranslations } from './use-translations';

/** One translated language on the delivery approval: its exportable counts. */
export interface LanguageApprovalOption {
  locale: string;
  pass: number;
  flag: number;
  heldBack: number;
  /** The GMC connection maps this locale (`config.locale_destinations`); false = delivery
   *  would skip it, so it can't be approved. */
  onConnection: boolean;
}

/**
 * The language gate on a delivery approval (workflow-service `approvedLocales`).
 *
 * Offers every locale the run was translated into, from the per-locale summary, and starts with
 * every locale that has at least one deliverable (PASS/FLAG) product AND a destination on the GMC
 * connection ticked. A locale the connection doesn't map is never approved — delivery would skip
 * it (the connection is the source of truth). `payload` is the
 * approval fragment: `approvedLocales` whenever the run HAS translations — `[]` included, which
 * means English only — and nothing at all when it has none, so an English-only run's approval is
 * byte-identical to before. Internal users only, like the rest of translation.
 */
export function useLanguageApproval(workflowId: string | null, enabled: boolean) {
  const canTranslate = useCanTranslateContent();
  const summary = useRunTranslations(workflowId ?? '', {
    enabled: enabled && canTranslate && workflowId !== null,
  });
  const delivery = useConnectionDeliveryLocales();
  const deliverableKey = delivery.codes.map((code) => code.toLowerCase()).join(',');
  const options = useMemo(() => {
    const onConnection = new Set(deliverableKey.split(',').filter(Boolean));
    return toOptions(summary.data ?? []).map((o) => ({
      ...o,
      onConnection: onConnection.has(o.locale.toLowerCase()),
    }));
  }, [summary.data, deliverableKey]);
  // `null` = untouched: the default selection follows the options as they load.
  const [picked, setPicked] = useState<string[] | null>(null);
  const approvable = new Set(options.filter((o) => o.onConnection).map((o) => o.locale));
  const selected = (
    picked ?? options.filter((o) => o.pass + o.flag > 0).map((o) => o.locale)
  ).filter((locale) => approvable.has(locale));

  const toggle = (locale: string) => {
    setPicked(
      selected.includes(locale) ? selected.filter((l) => l !== locale) : [...selected, locale],
    );
  };

  const payload: { approvedLocales?: string[] } =
    options.length > 0 ? { approvedLocales: selected } : {};

  return {
    options,
    selected,
    toggle,
    payload,
    /** For the "set it up on the connection" link; `undefined` without a GMC connection. */
    connectionId: delivery.connectionId,
    isLoading: (summary.isPending || delivery.isPending) && enabled,
  };
}

/**
 * One option per locale. A run translates its sample products under SAMPLE and then every product
 * again under FULL, so the two rows count mostly the same products; delivery ships ONE row per
 * product, the FULL one when it exists (integrations `_localized_rows_sql`). So a locale's counts
 * are its FULL row's, and its SAMPLE row's only while no FULL row exists — summing them
 * double-counts every sampled product (10 + 11 = "21 deliverable" for an 11-product run).
 */
export function toOptions(
  rows: RunTranslationSummary[],
): Omit<LanguageApprovalOption, 'onConnection'>[] {
  const byLocale = new Map<string, RunTranslationSummary>();
  for (const r of rows) {
    const current = byLocale.get(r.locale);
    if (!current || (r.enrichmentPhase === 'FULL' && current.enrichmentPhase !== 'FULL')) {
      byLocale.set(r.locale, r);
    }
  }
  return [...byLocale.values()].map((r) => ({
    locale: r.locale,
    pass: r.passCount,
    flag: r.flagCount,
    heldBack: r.failCount,
  }));
}
